import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken} from "../../api.js";

import Swal from "sweetalert2";

import CheckerViewShell from "./CheckerViewShell.jsx";

import { useRef, useEffect } from "react";

import { openInvoiceData } from "../../utils/invoiceDisplay.js";
import { openInvoicePdf } from "../../utils/openPdf.js";

function ExceptionDetailModal({row,onClose,onApprove,actionKey,}) {
  if (!row) {
    return null;
  }

  const exceptionData = row.exception_data || {};

  const allExceptions = Object.entries(exceptionData);

  return (
    <div
      className="processor-modal"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="processor-modal__card exception-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checker-exception-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Exception Detail</p>

            <h2 id="checker-exception-title">
              {row.exception_type || "Invoice Exceptions"}
            </h2>
          </div>

          <button
            className="ghost-button"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {row.message ? (
          <div className="mapping-note-card">
            {row.message}
          </div>
        ) : null}

        <div className="exception-list">
          {allExceptions.map(
            ([exceptionName, exceptionData]) => (
              <ExceptionTable
                key={exceptionName}
                row={row}
                exceptionName={exceptionName}
                exceptionData={exceptionData}
                onApprove={onApprove}
                actionKey={actionKey}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}

function ExceptionTable({row,exceptionName,exceptionData,onApprove,actionKey,}) {
  const isApproved =
    exceptionData?.status?.toLowerCase() === "approved";

  const isTaxException =
    exceptionName === "Mutual Tax Exclusivity";

  const isInvoiceValueException = [
    "No Invoice Value",
    "Invoice Value < Lower Limit",
    "Invoice Value > Upper Limit",
    "Migo > Invoice",
  ].includes(exceptionName);

  return (
    <div
      className={`exception-table-card ${
        isApproved
          ? "exception-table-card--approved"
          : ""
      }`}
    >
      <div className="exception-table-header">
        <div>
          <h3>{exceptionName}</h3>

          <span
            className={`exception-status ${
              isApproved
                ? "exception-status--approved"
                : "exception-status--pending"
            }`}
          >
            {isApproved ? "Approved" : "Pending"}
          </span>
        </div>
      </div>

      {isTaxException ? (
        <TaxExceptionTable
          row={row}
          exceptionName={exceptionName}
          exceptionData={exceptionData}
          onApprove={onApprove}
          actionKey={actionKey}
          disabled={isApproved}
        />
      ) : isInvoiceValueException ? (
        <InvoiceExceptionTable
          row={row}
          exceptionName={exceptionName}
          exceptionData={exceptionData}
          onApprove={onApprove}
          actionKey={actionKey}
          disabled={isApproved}
        />
      ) : (
        <GenericExceptionTable
          row={row}
          exceptionName={exceptionName}
          exceptionData={exceptionData}
          onApprove={onApprove}
          actionKey={actionKey}
          disabled={isApproved}
        />
      )}
    </div>
  );
}

function InvoiceExceptionTable({row,exceptionName,exceptionData,onApprove,actionKey,disabled,}) {
  const [invoiceValue, setInvoiceValue] = useState(
    exceptionData.InvoiceValue ?? ""
  );

  const actionId = `${row.InvoiceGroupKey}-${exceptionName}`;

  return (
    <>
      <table className="exception-detail-table">
        <tbody>
          <tr>
            <th>Exception</th>
            <td>{exceptionName}</td>
          </tr>

          <tr>
            <th>Message</th>
            <td>{exceptionData.message || "-"}</td>
          </tr>

          {exceptionData.MigoValue !== undefined ? (
            <tr>
              <th>MIGO Value</th>
              <td>{exceptionData.MigoValue}</td>
            </tr>
          ) : null}

          {exceptionData.LowerLimit !== undefined ? (
            <tr>
              <th>Lower Limit</th>
              <td>{exceptionData.LowerLimit}</td>
            </tr>
          ) : null}

          {exceptionData.UpperLimit !== undefined ? (
            <tr>
              <th>Upper Limit</th>
              <td>{exceptionData.UpperLimit}</td>
            </tr>
          ) : null}

          <tr>
            <th>Invoice Value</th>
            <td>
              <input
                type="number"
                step="0.01"
                value={invoiceValue}
                onChange={(event) => setInvoiceValue(event.target.value)}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="exception-table-actions">
        <button
          className="primary-button"
          type="button"
          disabled={
            disabled ||
            actionKey ===
              `${row.InvoiceGroupKey}-${exceptionName}`
          }
          onClick={() =>
            onApprove(
              row,
              exceptionName,
              invoiceValue
            )
          }
        >
          {disabled
            ? "Already Approved"
            : actionKey ===
                `${row.InvoiceGroupKey}-${exceptionName}`
              ? "Approving..."
              : "Approve Exception"}
        </button>
      </div>
    </>
  );
}

function TaxExceptionTable({
  row,
  exceptionName,
  exceptionData,
  onApprove,
  actionKey,
  disabled,
}) {
  const vendorGst = String(
    exceptionData.VendorGst || ""
  );

  const customerGst = String(
    exceptionData.CustomerGst || ""
  );

  const sameState =
    vendorGst.substring(0, 2) ===
    customerGst.substring(0, 2);

  const allowedTaxType = sameState
    ? "CGST_SGST"
    : "IGST";

  const actionId = `${row.InvoiceGroupKey}-${exceptionName}`;

  return (
    <>
      <table className="exception-detail-table">
        <tbody>
          <tr>
            <th>Vendor GST</th>
            <td>{vendorGst || "-"}</td>
          </tr>

          <tr>
            <th>Customer GST</th>
            <td>{customerGst || "-"}</td>
          </tr>

          <tr>
            <th>GST State Match</th>
            <td>
              {sameState ? "Same State" : "Different State"}
            </td>
          </tr>

          <tr>
            <th>Allowed Tax</th>
            <td>
              {sameState
                ? "CGST + SGST"
                : "IGST"}
            </td>
          </tr>

          <tr>
            <th>CGST</th>
            <td>{exceptionData.CGST || "-"}</td>
          </tr>

          <tr>
            <th>SGST</th>
            <td>{exceptionData.SGST || "-"}</td>
          </tr>

          <tr>
            <th>IGST</th>
            <td>{exceptionData.IGST || "-"}</td>
          </tr>
        </tbody>
      </table>

      <div className="exception-table-actions">
        <button
          className="primary-button"
          type="button"
          disabled={
            disabled ||
            actionKey ===
              `${row.InvoiceGroupKey}-${exceptionName}`
          }
          onClick={() =>
            onApprove(
              row,
              exceptionName,
              allowedTaxType
            )
          }
        >
          {disabled
            ? "Already Approved"
            : actionKey ===
                `${row.InvoiceGroupKey}-${exceptionName}`
              ? "Approving..."
              : `Approve ${
                  sameState
                    ? "CGST + SGST"
                    : "IGST"
                }`}
        </button>
      </div>
    </>
  );
}

export default function CheckerExceptionApprovalView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const summary = useMemo(
    () => ({
      count: rows.length,
      migoInvoice: rows.filter((row) => row.exception_type === "Migo>Invoice").length,
      withResponse: rows.filter((row) => row.unique_name).length,
    }),
    [rows],
  );

  const menuRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-exceptioninvoice-pending/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok) {
        throw new Error(payload?.message || "Unable to load checker exceptions.");
      }

      setRows(payload?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load checker exceptions.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  // async function handleApprove(row, invoiceValue) {
  //   const exceptionData = row.exception_data || {};
  //   const migoValue = Number(exceptionData.MigoValue || 0);
  //   const enteredValue = Number(invoiceValue);

  //   if (Number.isNaN(enteredValue)) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "Invalid Invoice Value",
  //       text: "Please enter a valid invoice value.",
  //     });
  //     return;
  //   }

  //   if (enteredValue < migoValue) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "Invoice Value Too Low",
  //       text: `Invoice value must be equal to or greater than the MIGO value (${migoValue}).`,
  //     });
  //     return;
  //   }

  //   const confirmResult = await Swal.fire({
  //     title: "Approve Exception?",
  //     text: "Are you sure you want to approve this exception?",
  //     icon: "question",
  //     showCancelButton: true,
  //     customClass: {
  //       popup: "swal-on-top",
  //     },
  //   });

  //   if (!confirmResult.isConfirmed) {
  //     return;
  //   }

  //   setActionKey(`approve-${row.InvoiceGroupKey}`);

  //   try {
  //     const csrfToken = await ensureCsrfToken();

  //     const response = await fetch(
  //       "/api/approve-invoicevalue_migovalue-exception/",
  //       {
  //         method: "POST",
  //         credentials: "include",
  //         headers: {
  //           "Content-Type": "application/json",
  //           "X-CSRFToken": csrfToken,
  //         },
  //         body: JSON.stringify({
  //           InvoiceGroupKey: row.InvoiceGroupKey,
  //           InvoiceValue: enteredValue,
  //         }),
  //       }
  //     );

  //     const isJson = response.headers
  //       .get("content-type")
  //       ?.includes("application/json");

  //     const payload = isJson ? await response.json() : null;

  //     if (!response.ok || !payload?.success) {
  //       throw new Error(payload?.message || "Approval failed.");
  //     }

  //     await Swal.fire({
  //       icon: "success",
  //       title: "Approved",
  //       text: payload.message || "Exception approved successfully.",
  //       timer: 1800,
  //       showConfirmButton: false,
  //     });

  //     setDetailRow(null);
  //     await loadRows();
  //   } catch (err) {
  //     Swal.fire({
  //       icon: "error",
  //       title: "Approval Failed",
  //       text: err.message || "Approval failed.",
  //     });
  //   } finally {
  //     setActionKey("");
  //   }
  // }

  async function handleApprove(
    row,
    exceptionName,
    approvalValue
  ) {
    const exceptionData =
      row.exception_data?.[exceptionName] || {};

    const actionId =
      `${row.InvoiceGroupKey}-${exceptionName}`;

    const confirmResult = await Swal.fire({
      title: "Approve Exception?",
      text: `Are you sure you want to approve "${exceptionName}"?`,
      icon: "question",
      showCancelButton: true,
      customClass: {
        popup: "swal-on-top",
      },
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setActionKey(actionId);

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch(
        "/api/approve-invoice-exception/",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            InvoiceGroupKey: row.InvoiceGroupKey,
            exception_name: exceptionName,
            approval_value: approvalValue,
            exception_data: exceptionData,
          }),
        }
      );

      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");

      const payload = isJson
        ? await response.json()
        : null;

      if (!response.ok || !payload?.success) {
        throw new Error(
          payload?.message || "Approval failed."
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text:
          payload.message ||
          "Exception approved successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      setDetailRow((previousRow) => {
        if (!previousRow) {
          return previousRow;
        }

        return {
          ...previousRow,
          exception_data: {
            ...previousRow.exception_data,

            [exceptionName]: {
              ...previousRow.exception_data?.[exceptionName],
              status: "approved",
            },
          },
        };
      });

    loadRows()

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Approval Failed",
        text: err.message || "Approval failed.",
      });

    } finally {
      setActionKey("");
    }
  }

  async function handleReject(row) {
    const confirmResult = await Swal.fire({
      title: "Reject Exception?",
      text: "Are you sure you want to reject this exception?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setActionKey(`reject-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/reject-invoicevalue_migovalue-exception/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_group_key: row.InvoiceGroupKey,
        }),
      });

      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");

      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.status !== "success") {
        throw new Error(payload?.message || "Unable to reject exception.");
      }

      await Swal.fire({
        icon: "success",
        title: "Rejected",
        text: payload.message || "Exception rejected successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      await loadRows();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Rejection Failed",
        text: err.message || "Unable to reject exception.",
      });
    } finally {
      setActionKey("");
    }
  }


  function refreshPendingActions() {
    loadRows();
  }

  return (
    <CheckerViewShell
          title="Checker Exception Approval"
          intro="Please Approve/Reject Invoice exceptions"
          metrics={[
            { label: "Queue", value: summary.count },
            
          ]}
          actions={
            <button
              className="ghost-button processor-view__refresh"
              type="button"
              onClick={refreshPendingActions}
            >
              Refresh
            </button>
          }
        >
          <section className="processor-view">
            

            {status ? <div className="config-banner config-banner--success">{status}</div> : null}
            {error ? <div className="config-banner config-banner--error">{error}</div> : null}

            

            <div className="workspace-panel">
              <div className="processor-table__header">
                <div>
                  <p className="eyebrow">Exception queue</p>
                </div>
                <button className="ghost-button" type="button" onClick={loadRows} disabled={loading}>
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              {loading ? (
                <p className="config-note">Loading exception approvals…</p>
              ) : (
                <div className="config-table processor-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th>Vendor Code</th>
                        <th>Vendor Name</th>
                        <th>Invoice Number</th>
                        <th>Message</th>
                        <th>File</th>
                        <th>Response</th>
                        <th>Exception</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((row) => (
                          <tr key={row.InvoiceGroupKey}>
                            <td>{row.InvoiceId}</td>
                            <td>{row.VendorCode || "-"}</td>
                            <td>{row.VendorName || "-"}</td>
                            <td>{row.InvoiceNo || "-"}</td>
                            <td>{row.message || "-"}</td>
                            <td>
                              {row.unique_name ? (
                                <button
                                  type="button"
                                  onClick={() => openInvoicePdf(row.InvoiceId)}
                                >
                                  📎
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {row.unique_name ? (
                                <button
                                  onClick={() =>
                                    openInvoiceData(
                                      `${row.unique_name}.json`
                                    )
                                  }
                                >
                                  Data
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <button
                                className="ghost-button"
                                type="button"
                                onClick={() => setDetailRow(row)}
                              >
                                View Exceptions
                              </button>
                            </td>
                            <td>
                              <div className="action-dropdown-wrapper">
                                <button
                                  type="button"
                                  className="action-menu-button"
                                  onClick={(e) => {

                                    if (
                                      menuPosition?.row?.InvoiceGroupKey  === row.InvoiceGroupKey 
                                    ) {
                                      setMenuPosition(null);
                                      return;
                                    }

                                    const rect = e.currentTarget.getBoundingClientRect();

                                    setMenuPosition({
                                      row,
                                      top: rect.bottom + window.scrollY,
                                      left: rect.left + window.scrollX,
                                    });
                                  }}
                                >
                                  Actions ▼
                                </button>                         
                              </div>
                            </td>
                            
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9">No invoices found in the checker exception queue.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <ExceptionDetailModal
              row={detailRow}
              onClose={() => setDetailRow(null)}
              onApprove={handleApprove}
              actionKey={actionKey}
            />

            {menuPosition && (
              <div
                className="global-action-menu"
                style={{
                  position: "fixed",
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: 999999,
                }}
              >
                {/* <button
                  type="button"
                  disabled={
                    menuPosition.row.exception_type !== "Migo>Invoice" ||
                    actionKey === `approve-${menuPosition.row.InvoiceGroupKey}`
                  }
                  onClick={() => {
                    setDetailRow(menuPosition.row);
                    setMenuPosition(null);
                  }}
                >
                  {actionKey === `approve-${menuPosition.row.InvoiceGroupKey}`
                    ? "Approving..."
                    : "✓ Approve"}
                </button> */}

                <button
                  type="button"
                  disabled={
                    actionKey === `reject-${menuPosition.row.InvoiceGroupKey}`
                  }
                  onClick={() => {
                    handleReject(menuPosition.row);
                    setMenuPosition(null);
                  }}
                >
                  {actionKey === `reject-${menuPosition.row.InvoiceGroupKey}`
                    ? "Rejecting..."
                    : "✕ Reject"}
                </button>
    
              </div>
            )}
          </section>

          
          
    </CheckerViewShell>
  );
}
